import { BrowserRouter, Link, Navigate, NavLink, Route, Routes } from 'react-router';
import { HelloPage } from './HelloPage';
import { AiUsagePage } from './pages/AiUsage/AiUsagePage';
import { AuditLogsPage } from './pages/AuditLogs/AuditLogsPage';
import { ConversionPage } from './pages/Conversion/ConversionPage';
import { FeatureUsagePage } from './pages/FeatureUsage/FeatureUsagePage';
import { KpiDashboardPage } from './pages/Kpi/KpiDashboardPage';
import { TrialListPage } from './pages/Trials/TrialListPage';
import { WorkspaceDetailPage } from './pages/Workspaces/WorkspaceDetailPage';
import { WorkspaceListPage } from './pages/Workspaces/WorkspaceListPage';

/**
 * admin-ops-web ルート (Phase 1 + Phase 2 #196 + Phase 3 #197 + Phase 4 #200 + Phase 5 #198)。
 * sales_counter 単体前提（#1479、カウンターシリーズ4製品構想は2026-07-02撤退）。
 *
 * VITE_ADMIN_OPS_API_BASE_URL が未設定なら同一 origin の `/api` を使う。
 *
 * Routes:
 *   - /          → /kpi へ redirect（Phase 3 デフォルト）
 *   - /hello     → Phase 1 Foundation 疎通確認
 *   - /kpi       → Phase 3 KPI ダッシュ
 *   - /workspaces → Phase 2 Workspace Health
 *   - /workspaces/:id → Phase 4 Workspace Drilldown (OPS-DR-1〜4)
 *   - /audit-logs → Phase 5 監査ログ横断
 *   - /ai-usage  → O30-3 (#1818) AI 利用（運営 Slack の AI 利用日次レポートのリンク先）
 *   - /feature-usage → O31-7 (#1959) 機能利用（運営 Slack の日次所見のリンク先）
 */
export default function App() {
  const apiBaseUrl =
    (typeof import.meta !== 'undefined' &&
      (import.meta as unknown as { env?: Record<string, string | undefined> }).env
        ?.VITE_ADMIN_OPS_API_BASE_URL) ||
    '';
  return (
    <BrowserRouter>
      <AppShell>
        <Routes>
          <Route path="/" element={<Navigate to="/kpi" replace />} />
          <Route path="/hello" element={<HelloPage apiBaseUrl={apiBaseUrl} />} />
          <Route path="/kpi" element={<KpiDashboardPage apiBaseUrl={apiBaseUrl} />} />
          {/* Phase 2 (#196) Workspace Health */}
          <Route path="/workspaces" element={<WorkspaceListPage apiBaseUrl={apiBaseUrl} />} />
          {/* Phase 5 (#198) Audit Cross-View */}
          <Route path="/audit-logs" element={<AuditLogsPage apiBaseUrl={apiBaseUrl} />} />
          {/* Phase 4 (#200) Workspace Drilldown — 末尾追加（catch-all の直前） */}
          <Route path="/workspaces/:id" element={<WorkspaceDetailPage apiBaseUrl={apiBaseUrl} />} />
          {/* SS-07 (#1705) トライアル中の介入リスト — 末尾追加（catch-all の直前） */}
          <Route path="/trials" element={<TrialListPage apiBaseUrl={apiBaseUrl} />} />
          {/* O30-3 (#1818) AI 利用 — 末尾追加（catch-all の直前）。Slack 日次レポートのリンク先 */}
          <Route path="/ai-usage" element={<AiUsagePage apiBaseUrl={apiBaseUrl} />} />
          {/* SS-14 (#1716) 商談なし有料化率 — 末尾追加（catch-all の直前） */}
          <Route path="/conversion" element={<ConversionPage apiBaseUrl={apiBaseUrl} />} />
          {/* O31-7 (#1959) 機能利用 — 末尾追加（catch-all の直前） */}
          <Route path="/feature-usage" element={<FeatureUsagePage apiBaseUrl={apiBaseUrl} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell>
    </BrowserRouter>
  );
}

function AppShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center gap-6 px-6 py-3">
          <Link to="/" className="text-sm font-semibold text-slate-900">
            AI Inbox Core Operations
          </Link>
          <div className="flex gap-4 text-sm">
            <NavLink
              to="/kpi"
              className={({ isActive }) =>
                isActive ? 'text-slate-900 font-medium' : 'text-slate-500 hover:text-slate-700'
              }
            >
              KPI
            </NavLink>
            <NavLink
              to="/workspaces"
              className={({ isActive }) =>
                isActive ? 'text-slate-900 font-medium' : 'text-slate-500 hover:text-slate-700'
              }
            >
              Workspaces
            </NavLink>
            <NavLink
              to="/trials"
              className={({ isActive }) =>
                isActive ? 'text-slate-900 font-medium' : 'text-slate-500 hover:text-slate-700'
              }
            >
              トライアル介入
            </NavLink>
            <NavLink
              to="/audit-logs"
              className={({ isActive }) =>
                isActive ? 'text-slate-900 font-medium' : 'text-slate-500 hover:text-slate-700'
              }
            >
              監査ログ横断
            </NavLink>
            <NavLink
              to="/ai-usage"
              className={({ isActive }) =>
                isActive ? 'text-slate-900 font-medium' : 'text-slate-500 hover:text-slate-700'
              }
            >
              AI 利用
            </NavLink>
            {/* SS-14 (#1716) 商談なし有料化率 — 末尾追加（既存の並びは変えない） */}
            <NavLink
              to="/conversion"
              className={({ isActive }) =>
                isActive ? 'text-slate-900 font-medium' : 'text-slate-500 hover:text-slate-700'
              }
            >
              商談なし有料化率
            </NavLink>
            {/* O31-7 (#1959) 機能利用 — 末尾追加（既存の並びは変えない・AC-1） */}
            <NavLink
              to="/feature-usage"
              className={({ isActive }) =>
                isActive ? 'text-slate-900 font-medium' : 'text-slate-500 hover:text-slate-700'
              }
            >
              機能利用
            </NavLink>
          </div>
        </div>
      </nav>
      {children}
    </div>
  );
}
